import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useOpensUp } from '../../../components/common/useOpensUp';
import { Avatar, Spinner } from '../../../components/ui';
import { organizationService } from '../../../services/organizationService';
import { stakeholderService } from '../../../services/stakeholderService';
import type { SubjectRef } from '../../../types/issues';
import { subjectKey } from './format';

/**
 * Finds a person or an organization to place on an issue. It searches the
 * directory rather than only what the issue already knows about, so an actor
 * nobody has thought of yet can still be positioned.
 */
export const SubjectPicker: React.FC<{
  onPick: (subject: SubjectRef) => void;
  /** Subjects already on the board */
  exclude?: string[];
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}> = ({ onPick, exclude = [], placeholder = 'Search people and organizations', autoFocus, className = '' }) => {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SubjectRef[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const excluded = useMemo(() => new Set(exclude), [exclude]);
  const latest = useRef(0);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const run = ++latest.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const [people, orgs] = await Promise.all([
        stakeholderService.getStakeholders({ search: term, limit: 8 }).catch(() => ({ data: [], total: 0 })),
        organizationService.getOrganizations({ search: term, limit: 8 }).catch(() => ({ data: [], total: 0 })),
      ]);
      if (run !== latest.current) return;
      const merged: SubjectRef[] = [
        ...(people.data || []).map((person: any) => ({
          type: 'stakeholder' as const,
          id: person.id,
          label: [person.firstName, person.lastName].filter(Boolean).join(' ').trim() || 'Unnamed',
        })),
        ...(orgs.data || []).map((org: any) => ({
          type: 'organization' as const,
          id: org.id,
          label: org.name || 'Unnamed',
        })),
      ].filter((subject) => !excluded.has(subjectKey(subject)));
      setResults(merged.slice(0, 10));
      setActive(0);
      setLoading(false);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query, excluded]);

  const pick = (subject: SubjectRef) => {
    onPick(subject);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const opensUp = useOpensUp(open && query.trim().length >= 2, fieldRef, 280);

  return (
    <div ref={fieldRef} className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.75} />
      <input
        type="search"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={placeholder}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 140)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(results.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && results[active]) {
            e.preventDefault();
            pick(results[active]);
          } else if (e.key === 'Escape') {
            setQuery('');
            setOpen(false);
          }
        }}
        className="block h-10 w-full rounded-full border-0 bg-neutral-100 pl-9 pr-9 text-[16px] text-ink placeholder:text-neutral-400 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:h-9 sm:text-[13px]"
      />
      {loading && <Spinner className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />}
      {open && query.trim().length >= 2 && (
        <ul
          id={listId}
          role="listbox"
          className={`absolute left-0 right-0 z-40 ${opensUp ? 'bottom-full mb-1.5' : 'mt-1.5'} max-h-[280px] animate-fade-up overflow-y-auto overscroll-contain rounded-2xl bg-white p-1 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]`}
        >
          {!results.length && !loading && <li className="px-3 py-2.5 text-[13px] text-neutral-400">No matches</li>}
          {results.map((subject, index) => (
            <li key={subjectKey(subject)} role="option" aria-selected={index === active}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(subject)}
                onMouseEnter={() => setActive(index)}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${
                  index === active ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                }`}
              >
                <Avatar name={subject.label} kind={subject.type} size="xs" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{subject.label}</span>
                <span className="flex-shrink-0 text-[11px] text-neutral-400">
                  {subject.type === 'organization' ? 'Organization' : 'Person'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SubjectPicker;
